import React from "react";
import { ArrowRight, TrendingUp, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getIcon } from "../utils/iconResolver";
import data from "../data/retailData.json";

const { palette: P, howItWorks: hiw } = data;

const RetailHowItWorks = () => {
  const nav = useNavigate();
  return (
    <section style={{ background: P.bg, padding: "clamp(60px, 8vw, 96px) clamp(20px, 4vw, 40px)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>

        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 6vw, 64px)" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.10)", border: "1px solid rgba(93,162,162,0.25)", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
            <TrendingUp style={{ width: 14, height: 14, color: P.teal }} />
            <span style={{ color: P.teal, fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{hiw.badge}</span>
          </div>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 48px)", fontWeight: 900, letterSpacing: "-0.02em", color: P.ink, lineHeight: 1.1, marginBottom: 16 }}>
            {hiw.headline.main} <span style={{ color: P.teal }}>{hiw.headline.accent}</span>
          </h2>
          <p style={{ fontSize: "clamp(15px, 2vw, 18px)", color: P.muted, maxWidth: 560, margin: "0 auto", lineHeight: 1.7 }}>{hiw.description}</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 22 }}>
          {hiw.steps.map((step, i) => (
            <div key={i}
              style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 22, padding: "clamp(28px, 4vw, 38px)", position: "relative", transition: "all 0.3s", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 22px 52px rgba(0,0,0,0.09)"; e.currentTarget.style.borderColor = "rgba(93,162,162,0.28)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = P.border; }}>

              {/* Step badge */}
              <div style={{ position: "absolute", top: -14, left: 28, background: P.white, border: `2px solid ${step.color}`, borderRadius: 10, padding: "5px 13px", boxShadow: `0 4px 12px ${step.color}30` }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: step.color, letterSpacing: "0.08em" }}>STEP {step.step}</span>
              </div>

              <div style={{ width: 56, height: 56, background: step.bg, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: step.color, marginBottom: 20, marginTop: 8 }}>
                {getIcon(step.icon, { style: { width: 28, height: 28 } })}
              </div>
              <h3 style={{ fontSize: "clamp(17px, 2vw, 20px)", fontWeight: 800, color: P.ink, marginBottom: 12 }}>{step.title}</h3>
              <p style={{ color: P.muted, fontSize: "clamp(13px, 1.5vw, 15px)", lineHeight: 1.7, marginBottom: 26 }}>{step.description}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {step.checkpoints.map((c, ci) => (
                  <div key={ci} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <CheckCircle style={{ width: 16, height: 16, color: step.color, flexShrink: 0, marginTop: 1 }} />
                    <span style={{ color: P.ink2, fontSize: "clamp(12px, 1.4vw, 14px)", fontWeight: 500, lineHeight: 1.5 }}>{c}</span>
                  </div>
                ))}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: step.color, borderRadius: "0 0 22px 22px", opacity: 0.7 }} />
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 56 }}>
          <p style={{ color: P.muted, fontSize: "clamp(14px, 1.8vw, 16px)", marginBottom: 26 }}>{hiw.footer.note}</p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            {hiw.footer.buttons.map((btn, i) => (
              i === 0 ? (
                <button key={i} onClick={() => nav(btn.href)}
                  style={{ background: P.teal, color: "white", border: "none", padding: "clamp(12px, 2vw, 15px) clamp(28px, 4vw, 40px)", borderRadius: 12, fontSize: "clamp(13px, 1.5vw, 15px)", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, boxShadow: "0 8px 28px rgba(93,162,162,0.32)", transition: "all 0.25s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = P.tealDark; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 36px rgba(93,162,162,0.45)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = P.teal; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(93,162,162,0.32)"; }}>
                  {btn.label} <ArrowRight style={{ width: 17, height: 17 }} />
                </button>
              ) : (
                <button key={i} onClick={() => nav(btn.href)}
                  style={{ background: P.white, border: `1.5px solid ${P.border}`, color: P.ink2, padding: "clamp(12px, 2vw, 15px) clamp(28px, 4vw, 40px)", borderRadius: 12, fontSize: "clamp(13px, 1.5vw, 15px)", fontWeight: 700, cursor: "pointer", transition: "all 0.25s", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = P.teal; e.currentTarget.style.color = P.teal; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = P.border; e.currentTarget.style.color = P.ink2; }}>
                  {btn.label}
                </button>
              )
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RetailHowItWorks;